/**
 * «Остатки по скриншотам» — все остатки разом (27.10.2026,
 * docs/design/bank-shots-2026-10-26).
 *
 * Найденное на скриншотах сопоставляется со счетами и кредитами: по последним
 * цифрам карты, по банку и виду, по названию. Строка — как в разборе выписки:
 * галочка, «было», новый остаток. Разница записывается той же корректировкой,
 * что «Скорректировать остаток»: в траты не попадает. Незнакомый счёт — новый,
 * без галочки: добавится, только если отметить.
 */
import { useEffect, useState } from 'react';
import { formatMoney, neg, sub, ZERO, type Money, type PlainDate } from '@skoka/core';
import {
  adjustBalance, parseAmountInput, todayPd, updateLoan, type AppState,
} from './store.js';
import { createAccount } from './account-form.js';
import { AccountGlyph } from './account-glyph.js';
import { ImportBar, ImportRow } from './import-row.js';
import { SheetWrap } from './sheet.js';
import { plural } from './text.js';
import { flash } from './flash.js';
import { collect, readBankShots, type FoundAccount, type FoundLoan } from './bank-shots.js';
import { ShotReading } from './bank-shots-ui.js';

type Acc = AppState['accounts'][number];
type Loan = AppState['loans'][number];

export type ShotMatch =
  | { kind: 'account'; key: string; account: Acc; target: Money; found: FoundAccount }
  | { kind: 'loan'; key: string; loan: Loan; target: Money | null; found: FoundLoan }
  | { kind: 'new'; key: string; found: FoundAccount }
  | { kind: 'absent'; key: string; account: Acc };

const LOAN_WORD: Record<FoundLoan['kind'], RegExp> = {
  mortgage: /ипотек/i, car: /авто|машин/i, consumer: /кредит|наличн/i, installment: /рассрочк/i,
};
const last4 = (name: string): string | null => name.match(/(\d{4})(?!.*\d)/)?.[1] ?? null;

/** Сопоставить найденное со счетами и кредитами. Чистая функция — для проверок. */
export function matchShots(state: AppState, accounts: readonly FoundAccount[], loans: readonly FoundLoan[]): ShotMatch[] {
  const live = state.accounts.filter((a) => !a.archived);
  const taken = new Set<string>();
  const out: ShotMatch[] = [];
  for (const f of accounts) {
    const card = f.draft.kind === 'credit_card';
    const pool = live.filter((a) => !taken.has(a.id) && (a.kind === 'credit_card') === card);
    const digits = last4(f.draft.name);
    const byDigits = digits ? pool.filter((a) => last4(a.name) === digits) : [];
    const byBank = f.draft.icon ? pool.filter((a) => a.icon === f.draft.icon && (card || (a.kind === 'savings') === (f.draft.kind === 'savings'))) : [];
    const byName = pool.filter((a) => a.name.trim().toLowerCase() === f.draft.name.trim().toLowerCase());
    const hit = byDigits[0] ?? (byBank.length === 1 ? byBank[0] : undefined) ?? byName[0];
    if (!hit) { out.push({ kind: 'new', key: `n:${f.key}`, found: f }); continue; }
    taken.add(hit.id);
    const value = parseAmountInput(f.draft.balance);
    out.push({ kind: 'account', key: `a:${hit.id}`, account: hit, target: card ? neg(value) : value, found: f });
  }
  for (const f of loans) {
    const hit = state.loans.find((l) => l.kind !== 'credit_card' && !taken.has(l.id) && LOAN_WORD[f.kind].test(l.name));
    if (!hit) continue;
    taken.add(hit.id);
    // «К полному погашению» — с процентами: остаток основного долга из неё
    // здесь не выделить, такую строку правят в листе кредита.
    out.push({ kind: 'loan', key: `l:${hit.id}`, loan: hit, target: f.draft.withInterest ? null : parseAmountInput(f.draft.principal), found: f });
  }
  // Счета тех же банков, которых на скриншотах нет: видно, что их не трогаем.
  const banks = new Set(accounts.map((f) => f.draft.icon).filter(Boolean));
  for (const a of live) if (!taken.has(a.id) && a.icon && banks.has(a.icon)) out.push({ kind: 'absent', key: `x:${a.id}`, account: a });
  return out;
}

const signed = (m: Money): string => (m < ZERO ? `−${formatMoney(neg(m))}` : `+${formatMoney(m)}`);

/** Читает скриншоты и показывает, что обновится. */
export function BalanceShotsSheet({ state, files, onClose, onDone }: {
  state: AppState; files: File[]; onClose: () => void; onDone: () => void;
}) {
  const [read, setRead] = useState<{ accounts: FoundAccount[]; loans: FoundLoan[]; dropped: number; foreign: number } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    readBankShots(files, 'app')
      .then((pages) => { if (alive) setRead(collect(pages, state.today)); })
      .catch((e: unknown) => { if (alive) setFailed(e instanceof Error ? e.message : 'Не получилось прочитать скриншоты'); });
    return () => { alive = false; };
  }, [files]);
  return (
    <SheetWrap title="Остатки по скриншотам" onClose={onClose}>
      {failed ? <><div className="note warn">{failed}</div><button className="btn ghost" onClick={onClose}>Закрыть</button></>
        : !read ? <ShotReading files={files} hint="Остатки появятся здесь — обычно до 10 секунд" />
          : read.accounts.length + read.loans.length === 0
            ? <><div className="note warn">На скриншотах не нашлось ни счетов, ни кредитов. Сними главный экран банка, где видны карты и остатки.</div>
              <button className="btn ghost" onClick={onClose}>Закрыть</button></>
            : <ShotRows state={state} {...read} onClose={onClose} onDone={onDone} />}
    </SheetWrap>
  );
}

function ShotRows({ state, accounts, loans, dropped, foreign, onClose, onDone }: {
  state: AppState; accounts: FoundAccount[]; loans: FoundLoan[]; dropped: number; foreign: number;
  onClose: () => void; onDone: () => void;
}) {
  const [rows] = useState(() => matchShots(state, accounts, loans));
  const changed = (r: ShotMatch): boolean => (r.kind === 'account' ? r.target !== r.account.balance
    : r.kind === 'loan' ? r.target !== null && r.target !== r.loan.principal : false);
  const [on, setOn] = useState(() => new Set(rows.filter(changed).map((r) => r.key)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const toggle = (k: string) => setOn((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const picked = rows.filter((r) => on.has(r.key));
  const seen = rows.filter((r) => r.kind !== 'absent').length;

  const apply = async () => {
    setBusy(true); setErr(null);
    const today = todayPd() as PlainDate;
    try {
      for (const r of picked) {
        if (r.kind === 'account') await adjustBalance({ accountId: r.account.id, target: r.target, date: today, note: 'по скриншоту из банка' });
        if (r.kind === 'loan' && r.target !== null) await updateLoan(r.loan.id, { principal: r.target, principalAsOf: today });
        if (r.kind === 'new') await createAccount(r.found.draft);
      }
      if (picked.length > 0) flash(`Остатки обновлены: ${picked.length}`);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не получилось обновить');
      setBusy(false);
    }
  };

  return (
    <>
      <div className="imphead"><div>
        <b>На скриншотах — {plural(seen, 'счёт', 'счёта', 'счетов')}</b>
        {foreign > 0 && <small>Валютные счета пока не обновляются: SKOKA считает в рублях</small>}
        {dropped > 0 && <small>Часть экрана не прочиталась — проверь, все ли счета на месте</small>}
      </div></div>
      {rows.map((r) => {
        if (r.kind === 'absent') {
          return <ImportRow key={r.key} muted icon={<AccountGlyph account={r.account} />} title={r.account.name}
            sub="на скриншотах нет — не трогаем" amount={formatMoney(r.account.balance)} />;
        }
        if (r.kind === 'new') {
          const d = r.found.draft;
          return <ImportRow key={r.key} icon={<AccountGlyph account={{ kind: d.kind, ...(d.icon ? { icon: d.icon } : {}) }} />}
            title={d.name} sub="новый счёт — отметь, чтобы добавить" warn
            amount={formatMoney(parseAmountInput(d.balance))}
            check={{ on: on.has(r.key), label: `Добавить: ${d.name}`, onToggle: () => toggle(r.key) }} />;
        }
        if (r.kind === 'account') {
          const card = r.account.kind === 'credit_card';
          const diff = sub(r.target, r.account.balance);
          return <ImportRow key={r.key} icon={<AccountGlyph account={r.account} />} title={r.account.name}
            sub={diff === ZERO ? 'совпадает' : `${card ? 'долг был' : 'было'} ${formatMoney(card ? neg(r.account.balance) : r.account.balance)} · ${signed(diff)}`}
            amount={formatMoney(card ? neg(r.target) : r.target)} muted={diff === ZERO}
            check={{ on: on.has(r.key), label: `Обновить: ${r.account.name}`, onToggle: () => toggle(r.key), disabled: diff === ZERO }} />;
        }
        return <ImportRow key={r.key} title={r.loan.name}
          sub={r.target === null ? 'только «к погашению» с процентами — поправь в листе кредита'
            : r.target === r.loan.principal ? 'совпадает' : `долг был ${formatMoney(r.loan.principal)} · ${signed(sub(r.target, r.loan.principal))}`}
          warn={r.target === null}
          amount={formatMoney(r.target ?? r.loan.principal)} muted={r.target === null || r.target === r.loan.principal}
          check={{ on: on.has(r.key), label: `Обновить: ${r.loan.name}`, onToggle: () => toggle(r.key), disabled: !changed(r) }} />;
      })}
      {err && <div className="note warn">{err}</div>}
      <ImportBar hint="Остатки станут как в банке. Разница в траты не попадёт.">
        <button className="btn pri" disabled={busy || picked.length === 0} onClick={() => void apply()}>
          {busy ? 'Обновляю…' : picked.length === 0 ? 'Нечего обновлять' : `Обновить ${plural(picked.length, 'остаток', 'остатка', 'остатков')}`}</button>
        <button className="btn ghost" disabled={busy} onClick={onClose}>Не обновлять</button>
      </ImportBar>
    </>
  );
}
